import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase-config";
import { collection, query, where, getDocs, orderBy, doc, updateDoc, getDoc } from "firebase/firestore";
import loginBg from '../assets/svg/login.svg';
import "./EmployeeDashboard.css";

const statusColors = {
  "Validé": "#43a047",
  "Refusé": "#e53935",
  "En attente": "#ff9800"
};

function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const [yyyy, mm, dd] = dateStr.split('-');
  return `${dd}-${mm}-${yyyy}`;
}

function parseHistoryDate(date) {
  if (!date) return null;
  if (typeof date === 'string') return new Date(date);
  if (date instanceof Date) return date;
  if (date.seconds !== undefined) return new Date(date.seconds * 1000);
  if (typeof date.toDate === 'function') return date.toDate();
  return null;
}

function getHistoryArray(abs) {
  if (!abs.history || !Array.isArray(abs.history)) {
    return [{
      date: abs.statusUpdatedAt || abs.createdAt || new Date(),
      statut: abs.status,
      commentaire: abs.adminComment || ""
    }];
  }
  return [...abs.history].sort((a, b) => new Date(b.date) - new Date(a.date));
}

function EmployeeAbsenceList() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // Filtrer les demandes à afficher (non archivées)
  const now = new Date();
  const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
  const filteredRequests = requests.filter(req => {
    // On archive si la date de fin + 2 mois < aujourd'hui
    if (!req.endDate) return true;
    const [yyyy, mm, dd] = req.endDate.split('-');
    const endDate = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return endDate >= twoMonthsAgo;
  });

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;
      const q = query(
        collection(db, "absences"),
        where("employeeEmail", "==", user.email),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchRequests();
  }, []);

  return (
    <div className="dashboard-board-container" style={{
      minHeight: '100vh',
      background: `url(${loginBg}) center/cover no-repeat`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      overflow: 'hidden', // fond fixe
      position: 'relative'
    }}>
      <div className="dashboard-board-content" style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: 20,
        padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        minWidth: 320,
        maxWidth: 400,
        minHeight: 420,
        maxHeight: '80vh',
        textAlign: 'center',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflowY: 'auto' // scroll uniquement sur le bloc central
      }}>
        {/* Croix de fermeture */}
        <button onClick={() => navigate("/absence-request")}
          style={{position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#333', width: 36, height: 36, lineHeight: 1, zIndex: 2}}
          aria-label="Fermer"
        >
          ×
        </button>
        <h2 style={{marginBottom: 24}}>Demandes Effectuées</h2>
        {loading ? (
          <div>Chargement...</div>
        ) : filteredRequests.length === 0 ? (
          <div>Aucune demande enregistrée.</div>
        ) : (
          <div style={{width: '100%', maxWidth: 340}}>
            {filteredRequests.map(req => (
              <div key={req.id} style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 16,
                marginBottom: 14,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: 'pointer',
                position: 'relative'
              }}
                onClick={() => setSelected(req)}
              >
                <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>{req.type}</div>
                <div style={{fontSize: 14, color: '#444', marginBottom: 6}}>
                  Du {formatDateFR(req.startDate)} ({req.startPart})<br />
                  au {formatDateFR(req.endDate)} ({req.endPart})
                </div>
                <span style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: statusColors[req.status] || '#bbb',
                  color: '#fff',
                  borderRadius: 12,
                  padding: '2px 12px',
                  fontSize: 13,
                  fontWeight: 600
                }}>{req.status}</span>
              </div>
            ))}
          </div>
        )}
        {/* Popup détail demande */}
        {selected && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 32,
              minWidth: 320,
              maxWidth: 400,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              position: 'relative',
              display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}>
              <button onClick={() => setSelected(null)}
                style={{position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#333', width: 36, height: 36, lineHeight: 1, zIndex: 2}}
                aria-label="Fermer"
              >×</button>
              <h3 style={{marginBottom: 12}}>Demande d'absence - Détails</h3>
              {/* Historique des statuts */}
              <div style={{width: '100%', marginBottom: 12}}>
                {getHistoryArray(selected).map((h, idx) => (
                  <div key={idx} style={{fontSize: 13, color: '#555', marginBottom: 4, borderLeft: '4px solid #eee', paddingLeft: 8}}>
                    <b>{h.statut}</b> le {h.date && (parseHistoryDate(h.date)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) || '-')}
                    {h.commentaire && <><br /><span style={{color:'#888'}}>Commentaire : {h.commentaire}</span></>}
                  </div>
                ))}
              </div>
              <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>{selected.type}</div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 4}}><b>{selected.employeeName || selected.employeeEmail}</b></div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 6}}>
                Du {formatDateFR(selected.startDate)} ({selected.startPart})<br />
                au {formatDateFR(selected.endDate)} ({selected.endPart})
              </div>
              <div style={{fontSize: 13, color: '#888', marginBottom: 8}}>
                Demande émise le {selected.createdAt && selected.createdAt.toDate ? formatDateFR(selected.createdAt.toDate().toISOString().slice(0,10)) : "-"}
              </div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 10}}>
                <b>Commentaire salarié :</b><br />{selected.comment || <span style={{color:'#bbb'}}>Aucun</span>}
              </div>
              {/* Badge de statut gros et centré */}
              <div style={{marginTop: 18, marginBottom: 0, display: 'flex', justifyContent: 'center', width: '100%'}}>
                <span style={{
                  background: statusColors[selected.status] || '#bbb',
                  color: '#fff',
                  borderRadius: 16,
                  padding: '10px 32px',
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textAlign: 'center',
                  display: 'inline-block',
                  margin: '0 auto'
                }}>{selected.status}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AbsencePopup({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    async function fetchData() {
      const ref = doc(db, "absences", id);
      const snap = await getDoc(ref);
      if (snap.exists()) setData({ id: snap.id, ...snap.data() });
    }
    fetchData();
  }, [id]);
  if (!data) return null;
  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.25)',
      zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 32,
        minWidth: 320,
        maxWidth: 400,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center'
      }}>
        <button onClick={onClose}
          style={{position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#333', width: 36, height: 36, lineHeight: 1, zIndex: 2}}
          aria-label="Fermer"
        >×</button>
        <h3 style={{marginBottom: 12}}>Demande d'absence - Détails</h3>
        {/* Historique des statuts */}
        <div style={{width: '100%', marginBottom: 12}}>
          {getHistoryArray(data).map((h, idx) => (
            <div key={idx} style={{fontSize: 13, color: '#555', marginBottom: 4, borderLeft: '4px solid #eee', paddingLeft: 8}}>
              <b>{h.statut}</b> le {h.date && (parseHistoryDate(h.date)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) || '-')}
              {h.commentaire && <><br /><span style={{color:'#888'}}>Commentaire : {h.commentaire}</span></>}
            </div>
          ))}
        </div>
        <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>{data.type}</div>
        <div style={{fontSize: 14, color: '#444', marginBottom: 4}}><b>{data.employeeName || data.employeeEmail}</b></div>
        <div style={{fontSize: 14, color: '#444', marginBottom: 6}}>
          Du {formatDateFR(data.startDate)} ({data.startPart})<br />
          au {formatDateFR(data.endDate)} ({data.endPart})
        </div>
        <div style={{fontSize: 13, color: '#888', marginBottom: 8}}>
          Demande émise le {data.createdAt && data.createdAt.toDate ? formatDateFR(data.createdAt.toDate().toISOString().slice(0,10)) : "-"}
        </div>
        <div style={{fontSize: 14, color: '#444', marginBottom: 10}}>
          <b>Commentaire salarié :</b><br />{data.comment || <span style={{color:'#bbb'}}>Aucun</span>}
        </div>
        {/* Badge de statut gros et centré */}
        <div style={{marginTop: 18, marginBottom: 0, display: 'flex', justifyContent: 'center', width: '100%'}}>
          <span style={{
            background: statusColors[data.status] || '#bbb',
            color: '#fff',
            borderRadius: 16,
            padding: '10px 32px',
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: 1,
            textAlign: 'center',
            display: 'inline-block',
            margin: '0 auto'
          }}>{data.status}</span>
        </div>
      </div>
    </div>
  );
}

export default EmployeeAbsenceList; 